// webapp/src/lib/offlineQueueCore.js
//
// Pieces shared by the offline queues (conventionScoreQueue — NSF cells,
// ipscScoreQueue — IPSC op log): per-key async mutex, retry backoff, the
// 'saving' lease, the permanent-vs-transient response rule and the test-only
// in-memory storage. Pure helpers; no stores, no IndexedDB here.

export const BACKOFF_MS = [2_000, 5_000, 15_000, 60_000, 120_000];

// An entry marked 'saving' is leased to the send that claimed it. Other drain
// contexts skip it while the lease is fresh; once expired it is treated as an
// interrupted send (reload / crash / closed tab) and reclaimed.
export const SAVING_LEASE_MS = 60_000;

export function backoffMs(attempts) {
    const i = Math.min(Math.max(attempts - 1, 0), BACKOFF_MS.length - 1);
    return BACKOFF_MS[i];
}

// Per-key async mutex factory. Each queue owns its own lock map so two queues
// never serialise against each other.
export function createKeyLock() {
    const locks = new Map();
    return function withKeyLock(key, fn) {
        const prev = locks.get(key) || Promise.resolve();
        const run = prev.then(fn, fn);          // run fn once prev settles (success or failure)
        const tail = run.then(() => {}, () => {}); // non-throwing tail so the chain keeps flowing
        locks.set(key, tail);
        tail.then(() => { if (locks.get(key) === tail) locks.delete(key); });
        return run;
    };
}

// A response is permanent-fail only on a 4xx (except 429). Everything else that
// is not a 2xx — 5xx, 429, a network error (null / envelope) — is transient.
export function isPermanent(resp) {
    if (!resp) return false;
    const s = resp.status;
    return typeof s === 'number' && s >= 400 && s < 500 && s !== 429;
}

export function isSuccess(resp) {
    return !!resp && resp.status >= 200 && resp.status < 300;
}

export function toPlain(value) {
    try { return JSON.parse(JSON.stringify(value)); } catch (e) { return null; }
}

// Test-only in-memory storage with the same shape as the IndexedDB wrappers.
// `keyPath` = the entry property used as the key ('key' or 'opKey').
export function makeMemoryStorage(keyPath = 'key') {
    const m = new Map();
    return {
        getAll: async () => Array.from(m.values()),
        put: async (entry) => { m.set(entry[keyPath], entry); },
        delete: async (key) => { m.delete(key); },
        get: async (key) => (m.has(key) ? m.get(key) : null),
        _map: m,
    };
}
